import {useRouter} from 'next/router';
import {pathOr} from 'ramda';
import {useEffect, useMemo} from 'react';

import {NEW_COURSE_ID} from '@/src/app/constants/course';
import useCreateCourseActions from '@/src/app/store/createCourse/useCreateCourseActions';
import useRoutePaths from '@/src/hooks/useRoutePaths';

import CreateCourseProgressLine from '../components/CreateCourseProgressLine';

interface CreateCourseProgressLineContainerProps {
  currentStep: string;
}

const CreateCourseProgressLineContainer = ({
  currentStep,
}: CreateCourseProgressLineContainerProps) => {
  const {course, currentLang} = useCreateCourseActions();
  const {
    push,
    query: {courseId},
  } = useRouter();
  const {CREATOR_STUDIO_FIRST_STEP} = useRoutePaths();
  const {selectedLanguages} = course;

  // @ts-ignore
  useEffect(() => {
    if (courseId === NEW_COURSE_ID) {
      return push(CREATOR_STUDIO_FIRST_STEP.getLink(NEW_COURSE_ID));
    }
  }, [currentStep, currentLang]);

  const isSecondStepAvailable = useMemo(() => {
    return Boolean(course.id) && courseId !== NEW_COURSE_ID;
  }, [courseId, course]);

  const isLastPageAvailable = useMemo(() => {
    if (!selectedLanguages.length) return false;

    const topics = selectedLanguages.reduce((previous, current) => {
      return [...previous, ...pathOr([], [current, topics], course)];
    }, []);
    const isHasSavedTopic = topics.some((topic) => topic.id);

    return isHasSavedTopic && courseId !== NEW_COURSE_ID;
  }, [currentLang, course, courseId]);

  const isThirdPageAvailable = useMemo(() => {
    if (!selectedLanguages.length) return false;
    return selectedLanguages.some((language) => course[language]?.id);
  }, [selectedLanguages, course]);

  return (
    <CreateCourseProgressLine
      isSecondStepAvailable={isSecondStepAvailable || isThirdPageAvailable}
      isThirdPageAvailable={isThirdPageAvailable}
      isLastPageAvailable={isLastPageAvailable}
      currentStep={currentStep}
    />
  );
};

export default CreateCourseProgressLineContainer;
